import React from 'react';
import { Outlet } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useSystemSettings } from '@/hooks/useSystemSettings';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Settings, Home, RefreshCw, LogOut } from 'lucide-react';
import { Link } from 'react-router-dom';

interface MaintenanceWrapperProps {
  children?: React.ReactNode;
}

export const MaintenanceWrapper: React.FC<MaintenanceWrapperProps> = ({ children }) => {
  const { isSystemAdmin, signOut } = useAuth();
  const { maintenanceMode, maintenanceMessage } = useSystemSettings();

  // While loading settings, show nothing (let the page load normally)
  // If maintenance mode is enabled, show maintenance page
  // Otherwise, render children normally

  // If maintenance mode is enabled and user is not a system admin, show maintenance message
  if (maintenanceMode && !isSystemAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full mx-4">
          <Card>
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-yellow-100 flex items-center justify-center">
                <Settings className="h-6 w-6 text-yellow-600" />
              </div>
              <CardTitle className="text-xl">System Maintenance</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert className="border-yellow-200 bg-yellow-50">
                <AlertDescription className="text-center">{maintenanceMessage}</AlertDescription>
              </Alert>

              <div className="flex flex-col gap-3 pt-4">
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link to="/" className="flex-1">
                    <Button variant="outline" className="w-full">
                      <Home className="h-4 w-4 mr-2" />
                      Back to Home
                    </Button>
                  </Link>
                  <Button
                    onClick={() => window.location.reload()}
                    variant="secondary"
                    className="flex-1"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Reload
                  </Button>
                </div>
                <Button onClick={signOut} variant="ghost" className="w-full">
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign Out
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // If user is system admin or maintenance mode is disabled, render content
  // - When used as a React wrapper: render provided children
  // - When used in a Route element: render nested routes via <Outlet />
  return <>{children ?? <Outlet />}</>;
};
